import {
  Card,
  Checkbox,
  CrudMode,
  DataDisplay,
  Grid,
  ValidationErrors,
  pn,
  Stack,
  useElementBreakpoints,
} from "@compass/core";
import { Input, Select } from "components";
import { Class, ClassEditResource, Provider, ProviderViewResource, LkClassMode } from "generated/openApi";

import produce from "immer";

export const ClassBasicInfo = (props: {
  classModel: Class;
  setClassModel: (value: Class) => void;
  viewResource: ProviderViewResource;
  editResource: ClassEditResource;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
}) => {
    const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const nameField = () => {
    const propertyName = pn<Class>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.name}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.classModel.name}
        onChange={(e) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const identificationField = () => {
    const propertyName = pn<Class>("identification");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.identification}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.classModel.identification}
        onChange={(e) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.identification = e.target.value;
            })
          )
        }
      />
    );
  };

  const unitsField = () => {
    const propertyName = pn<Class>("units");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.units}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.classModel.units}
        type="number"
        onChange={(e) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.units = e.target.value as any
            })
          )
        }
      />
    );
  };

  const classLevelField = () => {
    const propertyName = pn<Class>("classLevelId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.classLevel?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={props.editResource.classLevels?.find((x) => x.id === props.classModel.classLevelId)}
        options={props.editResource.classLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.classLevelId = value?.id;
            })
          )
        }
      />
    );
  };

  const classTypeField = () => {
    const propertyName = pn<Class>("classTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.classType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={props.editResource.classTypes?.find((x) => x.id === props.classModel.classTypeId)}
        options={props.editResource.classTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.classTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<Class>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.classModel.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const classSectionField = () => {
    const propertyName = pn<Class>("classSection");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.classSection}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.classModel.classSection}
        onChange={(e) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.classSection = e.target.value;
            })
          )
        }
      />
    );
  };

  const onTrackField = () => {
    const propertyName = pn<Class>("onTrack");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.onTrack}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.classModel.onTrack}
        onChange={(e) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.onTrack = e.target.value;
            })
          )
        }
      />
    );
  };

  const classModeField = () => {
    const propertyName = pn<Class>("classModeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const selected = props.editResource.classModes?.find((x) => x.id === props.classModel.classModeId);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{selected?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={selected}
        options={props.editResource.classModes}
        getOptionLabel={(x: LkClassMode) => x.name}
        onChange={(e, value) =>
          props.setClassModel(
            produce(props.classModel, (draft) => {
              draft.classModeId = value?.id;
            })
          )
        }
      />
    );
  };

  const lockedField = () => {
    const propertyName = pn<Class>("isLocked");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.classModel.isLocked ? "Yes" : "No"}</DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={!!props.classModel.isLocked}
          onChange={(e) =>
            props.setClassModel(
              produce(props.classModel, (draft) => {
                draft.isLocked = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  return (
    <Card header={"Info"}>
      <Grid ref={breakpointsRef} container spacing="sm">
         <Grid item xs={largeFieldBreakpoints}>
        {nameField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {identificationField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {unitsField()}
      </Grid>

      <Grid item xs={largeFieldBreakpoints}>
        {schoolField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {classLevelField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {classTypeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {classSectionField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {onTrackField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {classModeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {lockedField()}
      </Grid>

      </Grid>
    </Card>
  );
};
